// EP-135 Tier 1 — Meeting create modal. Admin+ only; the page checks
// role before opening this modal.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/ui/Modal'
import { Button, Input, Select, Textarea } from '@/components/ui'
import { useCreateMeeting } from '@/hooks/useGovernance'
import { useNotificationStore } from '@/stores'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import type { MeetingType } from '@/types/governance'

export interface MeetingFormModalProps {
  isOpen: boolean
  onClose: () => void
}

export function MeetingFormModal({ isOpen, onClose }: MeetingFormModalProps) {
  const { t } = useTranslation('governance')
  const { t: tc } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('governance')

  const [meetingType, setMeetingType] = useState<MeetingType>('annual_general_meeting')
  const [title, setTitle] = useState('')
  const [meetingAt, setMeetingAt] = useState('')
  const [venue, setVenue] = useState('')
  const [agenda, setAgenda] = useState('')
  const [callNumber, setCallNumber] = useState(1)
  const [noticeLeadDays, setNoticeLeadDays] = useState(14)

  const createMeeting = useCreateMeeting()

  const handleSave = async () => {
    if (!title.trim() || !meetingAt) return
    try {
      await createMeeting.mutateAsync({
        meeting_type: meetingType,
        title: title.trim(),
        meeting_at: new Date(meetingAt).toISOString(),
        venue: venue.trim() || null,
        agenda: agenda.trim() || null,
        call_number: callNumber,
        notice_lead_days: noticeLeadDays,
      })
      setTitle('')
      setMeetingAt('')
      setVenue('')
      setAgenda('')
      setCallNumber(1)
      setNoticeLeadDays(14)
      onClose()
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('meetings.actions.new_meeting')}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!title.trim() || !meetingAt}
            loading={createMeeting.isPending}
            data-testid="btn-save-meeting"
          >
            {tc('actions.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Select
          label={t('meetings.labels.meeting_type')}
          value={meetingType}
          onChange={(e) => setMeetingType(e.target.value as MeetingType)}
          options={[
            { value: 'annual_general_meeting', label: t('meetings.types.annual_general_meeting') },
            { value: 'extraordinary', label: t('meetings.types.extraordinary') },
            { value: 'committee', label: t('meetings.types.committee') },
          ]}
          data-testid="input-meeting-type"
        />
        <Input
          label={t('meetings.labels.title')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          data-testid="input-title"
        />
        <Input
          label={t('meetings.labels.meeting_at')}
          type="datetime-local"
          value={meetingAt}
          onChange={(e) => setMeetingAt(e.target.value)}
          required
          data-testid="input-meeting-at"
        />
        <Input
          label={t('meetings.labels.venue')}
          value={venue}
          onChange={(e) => setVenue(e.target.value)}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label={t('meetings.labels.call_number')}
            type="number"
            min={1}
            max={2}
            value={String(callNumber)}
            onChange={(e) => setCallNumber(Number(e.target.value))}
          />
          <Input
            label={t('meetings.labels.notice_lead_days')}
            type="number"
            min={1}
            max={365}
            value={String(noticeLeadDays)}
            onChange={(e) => setNoticeLeadDays(Number(e.target.value))}
          />
        </div>
        <Textarea
          label={t('meetings.labels.agenda')}
          value={agenda}
          onChange={(e) => setAgenda(e.target.value)}
          rows={4}
        />
      </div>
    </Modal>
  )
}
